import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, GripVertical, Plus, Pencil } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Btn, Field, inputCls } from "@/pages/admin/Admin";
import { Uploader } from "@/pages/admin/Products";

export function ImageGallery({ images, onChange, testid = "gallery" }) {
  const [drag, setDrag] = useState(null);
  const [url, setUrl] = useState("");
  const move = (to) => {
    if (drag === null || drag === to) return;
    const next = [...images];
    const [it] = next.splice(drag, 1);
    next.splice(to, 0, it);
    onChange(next);
    setDrag(null);
  };
  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex flex-wrap gap-3">
        {images.map((u, i) => (
          <div key={u + i} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => move(i)}
            className={`relative w-24 h-24 border-2 cursor-move group ${drag === i ? "opacity-40" : ""} ${i === 0 ? "border-[#8C4F27]" : "border-[#E6DFD5]"}`} data-testid={`${testid}-item-${i}`}>
            <img src={fileUrl(u)} alt="" className="w-full h-full object-cover pointer-events-none" />
            <GripVertical size={14} className="absolute top-1 left-1 text-white drop-shadow" />
            {i === 0 && <span className="absolute bottom-0 inset-x-0 bg-[#8C4F27] text-white text-[9px] text-center">COVER</span>}
            <button type="button" onClick={() => onChange(images.filter((_, j) => j !== i))} aria-label="Delete photo" data-testid={`${testid}-delete-${i}`}
              className="absolute top-1 right-1 bg-white/90 p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Uploader accept="image/*" label="Add photo" onDone={(d) => onChange([...images, d.url])} testid={`${testid}-upload`} />
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="…or paste image URL" className={`${inputCls} w-64`} data-testid={`${testid}-url-input`} />
        <button type="button" onClick={() => { if (url.trim()) { onChange([...images, url.trim()]); setUrl(""); } }} className="text-xs underline" data-testid={`${testid}-url-add`}>Add URL</button>
        <span className="text-[11px] text-[#6E6259]">Drag photos to reorder · first = cover</span>
      </div>
    </div>
  );
}

export function VariantEditor({ variants, onChange, colors }) {
  const setV = (i, patch) => onChange(variants.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  const setO = (i, k, patch) => setV(i, { options: variants[i].options.map((o, j) => (j === k ? { ...o, ...patch } : o)) });
  return (
    <div className="space-y-3" data-testid="variant-editor">
      {variants.map((v, i) => (
        <div key={i} className="border border-[#E6DFD5] p-3 space-y-2 bg-[#FAFAFA]" data-testid={`variant-${i}`}>
          <div className="flex gap-2 items-center">
            <input value={v.name} onChange={(e) => setV(i, { name: e.target.value })} placeholder="Variant name, e.g. Leather Color" className={`${inputCls} flex-1`} data-testid={`variant-name-${i}`} />
            <button type="button" onClick={() => onChange(variants.filter((_, j) => j !== i))} aria-label="Delete variant" data-testid={`variant-delete-${i}`}><Trash2 size={14} /></button>
          </div>
          {v.options.map((o, k) => (
            <div key={k} className="flex flex-wrap gap-2 items-center pl-4" data-testid={`variant-${i}-option-${k}`}>
              <input value={o.label} onChange={(e) => setO(i, k, { label: e.target.value })} placeholder="Option" className={`${inputCls} w-36`} data-testid={`variant-${i}-option-${k}-label`} />
              <input type="number" value={o.delta || 0} onChange={(e) => setO(i, k, { delta: Number(e.target.value) })} placeholder="+ price" className={`${inputCls} w-28`} title="Extra price (IDR)" />
              <select value={o.color || ""} onChange={(e) => { const c = colors.find((x) => x.hex === e.target.value); setO(i, k, { color: e.target.value || undefined, label: o.label || c?.name || "" }); }}
                className={`${inputCls} w-40`} data-testid={`variant-${i}-option-${k}-color`}>
                <option value="">No color</option>
                {colors.map((c) => <option key={c.color_id} value={c.hex}>{c.name}</option>)}
              </select>
              {o.color && <span className="w-6 h-6 rounded-full border" style={{ background: o.color }} />}
              {o.image && <img src={fileUrl(o.image)} alt="" className="w-8 h-8 object-cover" />}
              <Uploader accept="image/*" label={o.image ? "Photo ✓" : "Photo"} onDone={(d) => setO(i, k, { image: d.url })} testid={`variant-${i}-option-${k}-photo`} />
              <button type="button" onClick={() => setV(i, { options: v.options.filter((_, j) => j !== k) })} aria-label="Delete option"><Trash2 size={12} /></button>
            </div>
          ))}
          <button type="button" onClick={() => setV(i, { options: [...v.options, { label: "", delta: 0 }] })} className="text-xs underline pl-4" data-testid={`variant-${i}-add-option`}>+ Add option</button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...variants, { name: "", options: [{ label: "", delta: 0 }] }])} className="text-xs flex items-center gap-1 border border-[#3B220C] px-3 py-2" data-testid="add-variant-btn"><Plus size={12} />Add variant</button>
    </div>
  );
}

export function ColorLibrary({ colors, reload }) {
  const [f, setF] = useState({ name: "", hex: "#8C4F27" });
  const [editing, setEditing] = useState(null);
  const save = async () => {
    try { editing ? await api.put(`/admin/colors/${editing}`, f) : await api.post("/admin/colors", f); setF({ name: "", hex: "#8C4F27" }); setEditing(null); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (c) => { await api.delete(`/admin/colors/${c.color_id}`); reload(); };
  return (
    <div className="space-y-4" data-testid="color-library">
      <div className="flex gap-2 items-end">
        <Field label="Color name" className="flex-1"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="color-name-input" /></Field>
        <Field label="Color"><input type="color" value={f.hex} onChange={(e) => setF({ ...f, hex: e.target.value.toUpperCase() })} className="h-10 w-16 border" data-testid="color-hex-input" /></Field>
        <Btn onClick={save} data-testid="color-save-btn">{editing ? "Update" : "Add"}</Btn>
      </div>
      <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
        {colors.map((c) => (
          <div key={c.color_id} className="flex items-center gap-2 border border-[#E6DFD5] p-2 text-sm" data-testid={`color-row-${c.name}`}>
            <span className="w-6 h-6 rounded-full border" style={{ background: c.hex }} /><span className="flex-1">{c.name}</span><span className="font-mono text-[10px]">{c.hex}</span>
            <button onClick={() => { setEditing(c.color_id); setF({ name: c.name, hex: c.hex }); }} aria-label="Edit"><Pencil size={12} /></button>
            <button onClick={() => del(c)} aria-label="Delete"><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function useColors() {
  const [colors, setColors] = useState([]);
  const reload = () => api.get("/admin/colors").then((r) => setColors(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  return [colors, reload];
}
